import { Body, Button, IconButton, Tooltip } from '@assembly-js/design-system';
import clsx from 'clsx';
import { FieldArray, getIn, useFormikContext } from 'formik';

import { DndWrapper } from 'src/components/DndWrapper';
import { TextField } from 'src/components/TextField';
import { ProductStoreSchema } from 'src/routes/billing/store/schema';

const MAX_FEATURES = 12;

interface FeatureOverlayProps {
  value: string;
  name: string;
}

const FeatureOverlay = ({ value, name }: FeatureOverlayProps) => {
  return (
    <div className="flex items-center gap-2">
      <TextField
        name={name}
        value={value}
        fullWidth
        variant="outlined"
        placeholder="Describe feature"
        disabled
        InputProps={{
          classes: {
            root: 'hover:bg-gray-100',
            input: '!h-8 text-base',
            notchedOutline: '!border-[#053299]',
          },
        }}
      />
      <IconButton
        variant="minimal"
        className="bg-transparent hover:bg-gray-100 p-1.5 rounded cursor-pointer"
        icon="Close"
      />
    </div>
  );
};

interface Props {
  itemIndex: number;
  formikFeatureFieldName: string;
}

export const ProductStoreFeatures = ({
  itemIndex,
  formikFeatureFieldName,
}: Props) => {
  const { values, setFieldValue, handleChange, handleBlur, errors, touched } =
    useFormikContext<ProductStoreSchema>();
  const features = values.subscriptions[itemIndex]?.features || [];
  const isFeatureLimitExceeded = features.length >= MAX_FEATURES;

  return (
    <FieldArray name={formikFeatureFieldName}>
      {({ push, remove }) => (
        <div>
          <DndWrapper<string | undefined>
            items={features}
            draggableKey={`features-${itemIndex}`}
            onReorder={(newFeatures) =>
              setFieldValue(`subscriptions[${itemIndex}].features`, newFeatures)
            }
            shouldBeDragItem={features.length > 1}
            renderItem={(value, index, isDragging) => (
              <div className="flex items-start gap-1 group mb-1 h-8">
                <TextField
                  name={`${formikFeatureFieldName}[${index}]`}
                  autoFocus={!value}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={value}
                  fullWidth
                  variant="outlined"
                  placeholder="Describe feature"
                  error={
                    !!getIn(errors, `${formikFeatureFieldName}[${index}]`) &&
                    !!getIn(touched, `${formikFeatureFieldName}[${index}]`)
                  }
                  helperText={
                    !!getIn(errors, `${formikFeatureFieldName}[${index}]`) &&
                    !!getIn(touched, `${formikFeatureFieldName}[${index}]`)
                      ? getIn(errors, `${formikFeatureFieldName}[${index}]`)
                      : undefined
                  }
                  disabled={isDragging}
                  InputProps={{
                    classes: {
                      root: 'hover:bg-gray-100',
                      input: clsx('!h-8 text-base', {
                        'gray-300': isDragging,
                      }),
                      notchedOutline: clsx({
                        '!border-secondary-hover': isDragging,
                      }),
                    },
                  }}
                />
                <IconButton
                  variant="minimal"
                  onClick={() => remove(index)}
                  className={clsx(
                    'bg-transparent hover:bg-gray-100 p-1.5 rounded cursor-pointer active:bg-gray-300 h-8',
                    {
                      'text-secondary-hover': isDragging,
                    },
                  )}
                  disabled={isDragging}
                  icon="Close"
                />
              </div>
            )}
            renderOverlay={(value, index) => (
              <FeatureOverlay
                value={value || ''}
                name={`${formikFeatureFieldName}[${index}]`}
              />
            )}
          />
          <div className="flex">
            <Tooltip
              position="top"
              disabled={!isFeatureLimitExceeded}
              tooltipClassname="p-4 mt-4"
              content={
                <Body size="sm" className="text-black-heading">
                  You can add up to {MAX_FEATURES} features
                </Body>
              }
            >
              <span>
                <Button
                  label="Add feature"
                  onClick={() => push('')}
                  prefixIcon="Plus"
                  variant="secondary"
                  className={clsx(
                    'text-base my-3 border-0 hover:bg-transparent p-0',
                    {
                      'text-black-heading': !isFeatureLimitExceeded,
                      'text-light-gray': isFeatureLimitExceeded,
                    },
                  )}
                  disabled={isFeatureLimitExceeded}
                />
              </span>
            </Tooltip>
          </div>
        </div>
      )}
    </FieldArray>
  );
};
